// src/components/Enable2FA.jsx
import React, { useState, useEffect } from "react";
import twoFactorService from "../api/twoFactorService";
import api from "../api/api";

const Enable2FA = ({ initialEnabled = false, onStatusChange }) => {
    const [qrUrl, setQrUrl] = useState(null);
    const [code, setCode] = useState("");
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [enabled, setEnabled] = useState(initialEnabled);
    const [loading, setLoading] = useState(false);

    // =========================
    // Load actual 2FA status
    // =========================
    const loadStatus = async () => {
        try {
            const res = await api.get("/login/me");
            const status = res.data.isTwoFactorEnabled === true;
            setEnabled(status);
            onStatusChange?.(status);
        } catch (e) {
            console.error("Failed to load 2FA status:", e);
        }
    };

    useEffect(() => {
        loadStatus();
    }, []);

    // cleanup ObjectURL
    useEffect(() => {
        return () => {
            if (qrUrl) URL.revokeObjectURL(qrUrl);
        };
    }, [qrUrl]);

    // =========================
    // Generate QR
    // =========================
    const handleEnable = async () => {
        setMessage("");
        setError("");

        try {
            setLoading(true);

            if (qrUrl) URL.revokeObjectURL(qrUrl);
            setQrUrl(null);

            const url = await twoFactorService.setup2FA();
            setQrUrl(url);

            setMessage("📷 Scan the QR code and enter the 6-digit code.");
        } catch (err) {
            console.error("2FA setup error:", err);
            setError("❌ Failed to generate QR code.");
        } finally {
            setLoading(false);
        }
    };

    // =========================
    // Confirm 2FA
    // =========================
    const handleVerify = async () => {
        setMessage("");
        setError("");

        if (!code.trim()) {
            setError("Enter the code from the authenticator app.");
            return;
        }

        try {
            setLoading(true);

            await twoFactorService.confirm2FA(code.trim());

            setEnabled(true);
            setQrUrl(null);
            setCode("");

            setMessage("✅ Two-factor authentication enabled.");
            onStatusChange?.(true);

            await loadStatus();
        } catch (err) {
            console.error("2FA confirm error:", err);
            setError("❌ Invalid or expired code.");
        } finally {
            setLoading(false);
        }
    };

    // =========================
    // Disable 2FA
    // =========================
    const handleDisable = async () => {
        setError("");
        setMessage("");

        if (!window.confirm("Disable two-factor authentication?")) return;

        try {
            setLoading(true);

            await twoFactorService.disable2FA();

            if (qrUrl) URL.revokeObjectURL(qrUrl);
            setQrUrl(null);
            setCode("");
            setEnabled(false);

            setMessage("🛑 Two-factor authentication disabled.");
            onStatusChange?.(false);

            await loadStatus();
        } catch (err) {
            console.error("Disable 2FA error:", err);
            setError("❌ Failed to disable 2FA.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="mt-8 p-4 border rounded">
            <h2 className="text-xl font-semibold mb-2">
                Two-factor authentication (2FA)
            </h2>

            <p className="mb-2 text-sm">
                Status:{" "}
                <span className={enabled ? "text-green-700" : "text-gray-700"}>
                    {enabled ? "Enabled" : "Disabled"}
                </span>
            </p>

            <div className="flex gap-2 mb-4">
                <button
                    onClick={handleEnable}
                    disabled={loading}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-60"
                >
                    {enabled ? "Regenerate QR" : "Enable 2FA"}
                </button>

                {enabled && (
                    <button
                        onClick={handleDisable}
                        disabled={loading}
                        className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-60"
                    >
                        Disable 2FA
                    </button>
                )}
            </div>

            {qrUrl && (
                <>
                    <div className="my-4 text-center">
                        <img src={qrUrl} alt="2FA QR" className="mx-auto mb-2" />
                        <p className="text-sm text-gray-600">
                            Scan this QR code, then enter the code below.
                        </p>
                    </div>

                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            placeholder="Authenticator code"
                            value={code}
                            maxLength={6}
                            onChange={(e) =>
                                setCode(e.target.value.replace(/\D/g, ""))
                            }
                            className="border px-2 py-1 rounded flex-1 text-center tracking-widest"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                        />
                        <button
                            onClick={handleVerify}
                            disabled={loading || code.length !== 6}
                            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-60"
                        >
                            Confirm
                        </button>
                    </div>
                </>
            )}

            {message && <div className="mt-4 text-green-700 text-sm">{message}</div>}
            {error && <div className="mt-4 text-red-600 text-sm">{error}</div>}
        </div>
    );
};

export default Enable2FA;
